import React, { useEffect, useMemo, useState } from "react";

import { Button, Select } from "antd";
import { Slide } from "react-awesome-reveal";
import { toast } from "react-toastify";

import {
  countSelectOptions,
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../Common/UIComponents/SelectPopupScrollbar";

import { fetchSupportCompanies, formatSupportCompanyOptions } from "../../actions/login";

import genistaLogo from "../../assets/images/logo/genista-logo.svg";
import loginBgIcon from "../../assets/images/logo/login-bg-img.svg";
import loginMainIcon from "../../assets/images/logo/login-main-img.svg";

import "./supportLogin.css";
import "./login.css";

/**
 * Support login step 2: fetch company list, select a company, then submit.
 */
export default function SupportCompanyStep({
  firebaseIdToken,
  userEmail,
  onLogin,
  loginLoading = false,
  history,
}) {
  const [dropDownOptions, setDropDownOptions] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [companyDomain, setCompanyDomain] = useState("");
  const [userPayload, setUserPayload] = useState("");
  const [selectError, setSelectError] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const optionCount = useMemo(() => countSelectOptions(dropDownOptions), [dropDownOptions]);

  useEffect(() => {
    if (!firebaseIdToken || !userEmail) {
      return undefined;
    }

    let cancelled = false;
    setLoadingCompanies(true);

    fetchSupportCompanies(firebaseIdToken, userEmail).then((result) => {
      if (cancelled) {
        return;
      }
      if (!result.success) {
        toast.error(result.data || "Failed to load companies.");
        return;
      }
      setDropDownOptions(formatSupportCompanyOptions(result.data));
    }).finally(() => {
      if (!cancelled) {
        setLoadingCompanies(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [firebaseIdToken, userEmail]);

  const onSelect = (_value, option) => {
    if (selectError) {
      setSelectError(false);
    }
    setCompanyName(option.label);
    setCompanyDomain(option.company_domain);
    setUserPayload(option.user_payload);
  };

  const onSubmit = async () => {
    if (!companyName || !companyDomain) {
      setSelectError(true);
      return;
    }

    if (loginLoading || typeof onLogin !== "function") {
      return;
    }

    await onLogin({
      companyName,
      companyDomain,
      userPayload,
    });
  };

  return (
    <Slide direction="left" triggerOnce>
      <img
        src={genistaLogo}
        alt="Genista"
        className="logoLoginPage"
        onClick={() => history.push("/login")}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            history.push("/login");
          }
        }}
      />
      <div className="login-page">
        <div className="loginMainContainer">
          <div className="login-box">
            <div className="login-header">
              Select Company
            </div>

            <div className="login-header-subtitle">
              For internal use only. Choose the company you want to log in to.
            </div>

            <div className="support-login-company-domain">
              <form autoComplete="off" style={{ width: "unset", padding: "unset" }}> {/* eslint-disable-line */}
                <label htmlFor="support-login-select" className="Login__Input__Label">Company</label>
                <Select
                  id="support-login-select"
                  placeholder="Please select a company"
                  {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
                  loading={loadingCompanies}
                  disabled={loadingCompanies}
                  onSelect={onSelect}
                  options={dropDownOptions}
                  status={selectError ? "error" : undefined}
                  rootClassName="SupportLogin__Select"
                  classNames={selectPopupClassNames("SupportLogin__Select__Popup")}
                  virtual={false}
                  listHeight={256}
                  popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                    optionCount,
                    className: "SupportLogin__Select__PopupScroll",
                  })}
                />
              </form>
            </div>

            <div className="support-login-login">
              <Button
                type="primary"
                className="Login__Button"
                loading={loginLoading}
                disabled={loginLoading || loadingCompanies}
                onClick={onSubmit}
              >
                Log In
              </Button>
            </div>
          </div>

          <div className="loginImage">
            <img
              className="loginImageBubbles"
              src={loginBgIcon}
              alt="login"
            />
            <img
              className="loginImageCharacters"
              src={loginMainIcon}
              alt="login"
            />
          </div>
        </div>
      </div>
    </Slide>
  );
}
